import type { AgentAccent } from '@ralph/contracts'
import type { CSSProperties } from 'react'

/**
 * The agent accent palette.
 *
 * Six tokens, no free hex: a listing must not be able to paint outside the design system,
 * and the contract enforces that by making `accent` an enum (`AgentAccentSchema`). This
 * module is the only place that turns a token into a colour.
 *
 * **Why the tile carries its own background.** An accent that tints the page — coloured text
 * on a theme surface — has to be re-derived for the dark theme, and a value that passes AA on
 * warm paper usually fails on near-black. The accent here is instead the *fill* of a small
 * tile with a white glyph on it, so the contrast that matters is accent-vs-glyph and is
 * identical in both themes. `#ffffff` below is therefore not a palette colour that a re-brand
 * should move — it is the fixed other half of the ratio the six values were chosen against.
 *
 * Every value is at least **4.84:1** against that white glyph (slate 5.54, teal 4.84, plum
 * 5.21, moss 4.87, navy 5.19, clay 4.84), computed with the WCAG relative-luminance formula
 * rather than judged by eye — the previous set sat between 6:1 and 13.6:1, which is legible
 * but reads as six shades of ink rather than six colours, and the portal design wants the
 * tiles to carry actual hue. The band is deliberately narrow (4.84–5.54) so no agent's tile
 * looks heavier on the page than another's.
 *
 * `amber` is deliberately not a token. That channel used to mean "this is happening right
 * now"; the palette has since moved live state onto the primary, but a warm phosphor accent
 * would still compete with `--alert`, which is the only colour in the product allowed to mean
 * consequence. `clay` is the warm slot instead: an ochre brick that reads as earth.
 */
export const ACCENT_INK: Record<AgentAccent, string> = {
  slate: '#546b80',
  teal: '#0c8071',
  plum: '#ad4a78',
  moss: '#4f7d33',
  navy: '#3f6cb8',
  clay: '#a85f2e'
}

/**
 * Fill, glyph and depth for an identity tile.
 *
 * The two shadows are what stop a block of flat accent from reading as a printed swatch: an
 * inner lip along the bottom edge gives the tile a thickness, and a short cast shadow sits it
 * on the surface underneath. Both are mixed from `--shadow-ink`, which is warm in the light
 * theme and true black in the dark one, so the tile is not lit from a different room than the
 * panel it sits on.
 */
export const accentTileStyle = (accent: AgentAccent): CSSProperties => ({
  background: ACCENT_INK[accent],
  color: '#ffffff',
  boxShadow: `inset 0 -2px 4px color-mix(in srgb, var(--shadow-ink) 22%, transparent),
    0 3px 10px -3px color-mix(in srgb, var(--shadow-ink) 32%, transparent)`
})

/**
 * The agent's colour, handed to `.accent-wash` (see `styles/surfaces.css`).
 *
 * There used to be a second wash here — a `color-mix` against the raised-surface token,
 * returned as a background string — which meant two different gradients could claim to be
 * "that agent's panel". The class is the survivor because it mixes toward `--card-solid`
 * rather than toward a compatibility alias, and because the gradient itself belongs with the
 * other surface recipes rather than in a component's inline style. This function exists only
 * to spell the custom property once, so a call site cannot typo it into silence — a misspelt
 * `--wash-accent` does not fail, it falls back to the primary and every agent looks the same.
 *
 * The return type intersects the property in rather than asserting it: `CSSProperties` has no
 * index signature, and an assertion would also swallow a genuine mistake in the object beside
 * it.
 */
export const accentVars = (accent: AgentAccent): CSSProperties & Record<'--wash-accent', string> => ({
  '--wash-accent': ACCENT_INK[accent]
})
